import { useEffect, useState } from "react";
import { Menu, X, Phone, ArrowRight, Facebook, ChevronDown } from "lucide-react";

const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61589883083004";
import Logo from "@/components/site/Logo";

const NAV = [
  {
    label: "Flooring",
    href: "/#materials",
    children: [
      { label: "LVP Flooring", href: "/flooring-contractor-st-louis#lvp" },
      { label: "Laminate Flooring", href: "/flooring-contractor-st-louis#laminate" },
      { label: "Tile Installation", href: "/flooring-contractor-st-louis#tile" },
      { label: "All Services", href: "/services" },
    ],
  },
  {
    label: "Areas",
    href: "/#areas",
    children: [
      { label: "O'Fallon, MO", href: "/flooring-installation-ofallon-mo" },
      { label: "St. Peters, MO", href: "/flooring-installation-st-peters-mo" },
      { label: "St. Charles, MO", href: "/flooring-installation-st-charles-mo" },
      { label: "Wentzville, MO", href: "/flooring-installation-wentzville-mo" },
      { label: "Chesterfield, MO", href: "/flooring-installation-chesterfield-mo" },
      { label: "Kirkwood, MO", href: "/flooring-installation-kirkwood-mo" },
      { label: "Ballwin, MO", href: "/flooring-installation-ballwin-mo" },
      { label: "Clayton, MO", href: "/flooring-installation-clayton-mo" },
    ],
  },
  { label: "Portfolio", href: "/#portfolio" },
  { label: "About", href: "/#about" },
  { label: "Process", href: "/#process" },
  { label: "Reviews", href: "/#testimonials" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", href: "/#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-50">
      <div
        className={`transition-all duration-400 ${
          scrolled
            ? "bg-stone-50/95 backdrop-blur-xl shadow-[0_4px_20px_-12px_rgba(28,25,23,0.18)]"
            : "bg-stone-50/80 backdrop-blur-md"
        } border-b border-stone-900/5`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-18" : "h-22"} py-3`}>
            {/* BRAND */}
            <a href="/" data-testid="brand-link" className="flex items-center gap-2.5 sm:gap-3.5 group shrink min-w-0">
              <Logo size={scrolled ? "md" : "lg"} className="shrink-0" />
              <div className="flex flex-col leading-tight min-w-0">
                <span className="font-display text-[13px] sm:text-[15px] md:text-lg lg:text-xl xl:text-[22px] text-stone-900 font-bold tracking-tight leading-tight">
                  M<span className="text-orange-600">&amp;</span>L Exterior and Interior Design
                </span>
                <span className="hidden sm:inline text-[10px] md:text-[11px] lg:text-[12px] text-stone-500 mt-1 font-semibold tracking-[0.14em] uppercase whitespace-nowrap">
                  Flooring · LVP · Laminate · Tile · Est. 2013
                </span>
              </div>
            </a>

            {/* NAV */}
            <nav className="hidden lg:flex items-center gap-6">
              {NAV.map((n) =>
                n.children ? (
                  <div
                    key={n.href}
                    className="relative group"
                    data-testid={`nav-group-${n.label.toLowerCase()}`}
                  >
                    <a
                      href={n.href}
                      data-testid={`nav-${n.label.toLowerCase()}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-stone-700 hover:text-orange-600 transition-colors whitespace-nowrap py-2"
                    >
                      {n.label}
                      <ChevronDown
                        size={13}
                        strokeWidth={2.25}
                        className="mt-0.5 transition-transform group-hover:rotate-180"
                      />
                    </a>
                    <div
                      data-testid={`nav-submenu-${n.label.toLowerCase()}`}
                      className="invisible opacity-0 group-hover:visible group-hover:opacity-100 absolute left-0 top-full pt-2 min-w-[220px] transition-opacity duration-150 z-50"
                    >
                      <div className="bg-stone-50 border border-stone-200 rounded-2xl shadow-[0_20px_50px_-20px_rgba(28,25,23,0.25)] py-2 overflow-hidden">
                        {n.children.map((c) => (
                          <a
                            key={c.href}
                            href={c.href}
                            data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="block px-5 py-2.5 text-sm font-medium text-stone-700 hover:text-orange-600 hover:bg-orange-50/60 transition-colors"
                          >
                            {c.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <a
                    key={n.href}
                    href={n.href}
                    data-testid={`nav-${n.label.toLowerCase()}`}
                    className="text-sm font-medium text-stone-700 hover:text-orange-600 transition-colors whitespace-nowrap"
                  >
                    {n.label}
                  </a>
                )
              )}
            </nav>

            {/* CTA */}
            <div className="flex items-center gap-1">
              <a
                href="tel:+16368496681"
                data-testid="header-phone"
                className="hidden xl:inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-stone-800 hover:text-orange-600 transition-colors whitespace-nowrap"
              >
                <Phone size={14} strokeWidth={2.25} />
                636-849-6681
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="header-facebook"
                aria-label="Follow M&L Exterior and Interior Design on Facebook"
                className="hidden md:inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-stone-700 hover:text-[#1877F2] transition-colors group"
              >
                <Facebook size={15} strokeWidth={2.25} className="group-hover:fill-[#1877F2] group-hover:text-[#1877F2] transition-colors" />
                <span className="hidden xl:inline">Facebook</span>
              </a>
              <a
                href="/#contact"
                data-testid="header-cta"
                className="hidden md:inline-flex items-center gap-1.5 px-4 py-2.5 bg-stone-900 text-stone-50 text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors duration-300 group whitespace-nowrap"
              >
                Free Quote
                <ArrowRight
                  size={15}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </a>
              <button
                onClick={() => setOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
                className="lg:hidden p-2 text-stone-800"
              >
                {open ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE DRAWER */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-stone-50 border-t border-stone-200 shadow-xl">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            {NAV.map((n) => (
              <div key={n.href}>
                <a
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="block py-3 border-b border-stone-200 text-lg font-medium text-stone-800"
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </a>
                {n.children && (
                  <div className="pl-4 border-b border-stone-200 py-2 flex flex-col">
                    {n.children.map((c) => (
                      <a
                        key={c.href}
                        href={c.href}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-sub-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="py-2 text-[15px] text-stone-600 hover:text-orange-600 transition-colors"
                      >
                        <span className="text-orange-500 mr-2">›</span>
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="flex flex-col gap-3 pt-5">
              <a href="tel:+16368496681" className="flex items-center gap-2 text-base text-stone-700" data-testid="mobile-phone">
                <Phone size={16} /> 636-849-6681
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-facebook"
                className="flex items-center gap-2 text-base text-stone-700 hover:text-[#1877F2] transition-colors"
              >
                <Facebook size={16} /> Follow on Facebook
              </a>
            </div>
            <a
              href="/#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta"
              className="mt-4 inline-flex items-center justify-center px-5 py-4 bg-stone-900 text-stone-50 text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors"
            >
              Free Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
